import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CalendarOptions, DatesSetArg } from '@fullcalendar/core';
import { FullCalendarComponent } from '@fullcalendar/angular';
import dayGridPlugin from '@fullcalendar/daygrid';
import bootstrap5Plugin from '@fullcalendar/bootstrap5';
import {
  PortalEventList,
  PortalEventsDateAndSearchFilter
} from '@tix/event-buyer/services';
import { PortalEventsFilter } from '@tix/event-buyer/services';
import { PortalEventsService } from '@tix/event-buyer/services';

import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import {
  TixEventInstanceMedialFile,
  TixEventInstanceTicketConfig
} from '@tix/data-access';
import {
  dollarFigureToNumber,
  formatAsDollarFigure,
  isEventAlreadyPassed
} from '@tix/shared/utils';
import { showTooltipOnTheLeft } from '@tix/shared/utils';
import { BuyerService } from 'libs/event-buyer/components/src/lib/services/buyer.service';
import { HelperDateService } from '@tix/util-common';
import { TixAnalyticsService } from '@tix/shared/analytics';
@Component({
  selector: 'tix-event-calendar-view-front',
  templateUrl: './event-calendar-view-front.component.html',
  styleUrls: ['./event-calendar-view-front.component.scss']
})
export class TixEventCalendarViewFrontComponent implements OnInit, OnDestroy {
  @ViewChild('calendar') calendarComponent: FullCalendarComponent;

  events: PortalEventList = [];
  filter: PortalEventsFilter;
  calendarViewStartDate: Date;
  calendarViewLastDate: Date;
  isLoading = false;
  venueId: string;
  #helperDateService = new HelperDateService();

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    private portalEventService: PortalEventsService,
    private buyerService: BuyerService,
    private tixAnalytics: TixAnalyticsService
  ) {}
  calendarOptions: CalendarOptions = {
    plugins: [bootstrap5Plugin, dayGridPlugin],
    initialView: 'dayGridMonth',
    weekends: true,
    height: 'auto',
    eventOrder: 'start,end',
    themeSystem: 'bootstrap5',
    buttonIcons: {
      prev: 'arrow-left',
      next: 'arrow-right'
    },
    datesSet: this.handleDatesSet.bind(this),
    headerToolbar: {
      start: 'title prev,next',
      end: 'dayGridMonth dayGridWeek'
    },
    customButtons: {
      next: {
        click: this.nextMonth.bind(this)
      },
      prev: {
        click: this.prevMonth.bind(this)
      }
    },
    viewDidMount: args => {
      if (args.view.type === 'dayGridMonth') {
        this.tixAnalytics.captureEvent('switch_to_month_view', {
          venueId: this.venueId
        });
      } else if (args.view.type === 'dayGridWeek') {
        this.tixAnalytics.captureEvent('switch_to_week_view', {
          venueId: this.venueId
        });
      }
    },
    eventClick: info => {
      this.tixAnalytics.captureEvent('click_event', {
        venueId: this.venueId,
        eventInstanceId: info.event.extendedProps?.['eventInstanceId']
      });
    }
  };

  handleDatesSet(arg: DatesSetArg) {
    const firstDayOfMonth = new Date(arg.start);
    const lastDayOfMonth = new Date(arg.end);

    lastDayOfMonth.setDate(lastDayOfMonth.getDate() - 1);
    this.calendarViewStartDate = firstDayOfMonth;
    this.calendarViewLastDate = lastDayOfMonth;
    this.tixAnalytics.captureEvent('select_date', { venueId: this.venueId });
  }

  async nextMonth() {
    const calendarApi = this.calendarComponent.getApi();
    calendarApi.next();
    this.tixAnalytics.captureEvent('next_month', { venueId: this.venueId });
    await this.getEventsPerMonth();
  }

  async prevMonth() {
    const calendarApi = this.calendarComponent.getApi();
    calendarApi.prev();
    this.tixAnalytics.captureEvent('previous_month', { venueId: this.venueId });
    await this.getEventsPerMonth();
  }

  getCurrentDate() {
    const calendarApi = this.calendarComponent?.getApi();
    const currentDate = calendarApi?.getDate();
    return currentDate;
  }

  ngAfterViewInit() {
    if (this.route.snapshot.params['venueId']) {
      this.venueId = this.route.snapshot.params['venueId'];
      this.getEventsPerMonth();
    } else {
      if (!this.venueId) {
        this.route.data.subscribe(data => {
          this.venueId = data['venueId'];
          this.getEventsPerMonth();
        });
      }
    }
  }

  showTootltip(eventDate: Date): boolean {
    return showTooltipOnTheLeft(eventDate);
  }

  OriginTopRight(eventDate: Date): boolean {
    const day = eventDate.getDay();
    return day === 6;
  }

  OriginTop(eventDate: Date): boolean {
    const day = eventDate.getDay();
    return day != 6 && day != 0;
  }
  OriginTopLeft(eventDate: Date): boolean {
    const day = eventDate.getDay();

    return day === 0;
  }

  openPurchasingPortal(eventInstanceId: string) {
    const url = this.router
      .createUrlTree(['/venue', this.venueId], {
        queryParams: {
          searchEvent: eventInstanceId,
          open: eventInstanceId,
          selectedEvent: eventInstanceId
        }
      })
      .toString();

    window.open(url, '_blank');
  }

  getEventImage(media: TixEventInstanceMedialFile[]) {
    return media.filter(media => {
      return media?.mediaFile?.fileType === 'Event Image';
    })[0]?.mediaFile?.location;
  }

  getAllTicketPrices(
    ticketConfigurations: TixEventInstanceTicketConfig[]
  ): number[] {
    const onlineConfigurations = ticketConfigurations.filter(ticket => {
      return (
        !ticket.TicketConfigurations.salesChannel ||
        ['both', 'online'].includes(
          ticket.TicketConfigurations.salesChannel.toLowerCase()
        )
      );
    });
    const ticketPrices = onlineConfigurations
      .map(config => {
        return dollarFigureToNumber(config.TicketConfigurations.ticketPrice);
      })
      .filter(price => price > 0);

    if (ticketPrices.length === 0) {
      return [0];
    } else {
      return ticketPrices;
    }
  }

  getMinTicketPrice(ticketsPrices: number[]): number {
    return Math.min(...ticketsPrices);
  }

  async getEventsPerMonth() {
    let currentDate = this.getCurrentDate();

    if (!currentDate) {
      currentDate = new Date();
    }

    this.isLoading = true;
    this.events = await this.portalEventService.getEventsForVenueWithFilter(
      this.venueId,
      new PortalEventsDateAndSearchFilter(
        '',
        1,
        '',
        this.calendarViewStartDate,
        this.calendarViewLastDate,
        100
      )
    );
    this.isLoading = false;
    this.calendarOptions.events = this.events?.map(event => {
      return {
        title: event.eventName || '',

        extendedProps: {
          portalEvent: event,
          eventImage: this.getEventImage(
            event.media as TixEventInstanceMedialFile[]
          ),
          minTicketPrice: formatAsDollarFigure(
            this.getMinTicketPrice(
              this.getAllTicketPrices(
                event.Tickets[0]
                  .Configurations as TixEventInstanceTicketConfig[]
              )
            )
          ),

          eventInstanceId: event.eventInstanceId,
          doorsOpen:
            event.doorsOpen != null
              ? moment(event.doorsOpen, 'HH:mm:ss').format('h:mm A')
              : '',
          rawDate: this.#helperDateService.getLocalDateAsUTC(event.date) || '',
          startTime:
            event.startTime != null
              ? moment(event.startTime, 'HH:mm:ss').format('h:mm A')
              : ''
        },
        start: this.#helperDateService.getLocalDateAsUTC(event.date) || ''
      };
    });
  }

  isEventPassed(date: Date) {
    return isEventAlreadyPassed(date);
  }

  ngOnInit(): void {
    this.buyerService.setShowListViewBtn(true);
  }

  ngOnDestroy(): void {
    this.buyerService.setShowListViewBtn(false);
  }
}
